import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import React, { JSX, useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { ECurrencyType } from '~/src/dash/api/exchange-rates';
import {
  formatCellValue,
  ITableCol,
  ITableMetric,
  ITableMetricValueTypes,
  ORDER_DIRECTION,
  OrderDirectionType,
  styleCellValue,
  TableGroupByKey,
  TableGroupByKeyForReportsDashboard,
  TableMetricKey,
  ValueType,
  valueTypes,
} from '~/src/dash/components/tables/common';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  Col,
  Cols,
  Row,
  Rows,
  Span,
  TKey,
  Text,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';

const getColumnAlignment = (valueType: ValueType | undefined) => {
  switch (valueType) {
    case valueTypes.NAME:
    case valueTypes.PERCENT_CHANGE:
      return 'flex-start';
    default:
      return 'flex-end';
  }
};

const activeArrow = (isHovered: boolean, orderDirection: OrderDirectionType) => {
  if (orderDirection === ORDER_DIRECTION.ASC) {
    return isHovered ? '▼' : '▲';
  } else {
    return isHovered ? '▲' : '▼';
  }
};

const inactiveArrow = (isHovered: boolean, defaultOrderDirection: OrderDirectionType) => {
  if (defaultOrderDirection === ORDER_DIRECTION.ASC && isHovered) {
    return '▲';
  }
  if (defaultOrderDirection === ORDER_DIRECTION.DESC && isHovered) {
    return '▼';
  }
  return '';
};

interface ISortRowsParams {
  rows: any[];
  key: string;
  direction: OrderDirectionType;
  isName?: boolean;
}

const sortRows = ({ rows, key, direction, isName = false }: ISortRowsParams) => {
  if (direction === ORDER_DIRECTION.ASC) {
    if (isName) {
      return rows.sort((a: any, b: any) => (a.Name !== b.Name ? (a.Name < b.Name ? -1 : 1) : 0));
    } else {
      return rows.sort((a: any, b: any) => a[key] - b[key]);
    }
  } else {
    if (isName) {
      return rows.sort((a: any, b: any) => (b.Name !== a.Name ? (b.Name < a.Name ? -1 : 1) : 0));
    } else {
      return rows.sort((a: any, b: any) => b[key] - a[key]);
    }
  }
};

const getDirection = (
  defaultDirection: OrderDirectionType,
  isActive: boolean,
  currentOrderDirection: OrderDirectionType,
) => {
  if (!isActive) {
    return defaultDirection;
  }
  if (currentOrderDirection === ORDER_DIRECTION.DESC) {
    return ORDER_DIRECTION.ASC;
  } else {
    return ORDER_DIRECTION.DESC;
  }
};

interface IColHeaderProps {
  label: string;
  alias?: string;
  isOrderedBy: boolean;
  isGroupBy?: boolean;
  isSelectable: boolean;
  // true makes it so that the table sorts only the data stored in state
  // false implies an api call and handle will be fired
  isTableControllingSort: boolean;
  valueType: ValueType;
  orderDirection: OrderDirectionType;
  defaultOrderDirection: OrderDirectionType;
  tooltipMessage?: string;
  headerStyle?: React.CSSProperties;
  onClick: () => void;
  tKey?: TKey;
}

const ColHeader = ({
  label,
  alias = '',
  onClick,
  isOrderedBy,
  isSelectable,
  valueType,
  orderDirection,
  defaultOrderDirection,
  isGroupBy = false,
  tooltipMessage = '',
  headerStyle = {},
  tKey,
}: IColHeaderProps): JSX.Element => {
  const { t } = usePublicaTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const colName = tKey ? t(tKey) : alias || label;
  const onHover = () => setIsHovered(true);
  const onLeave = () => setIsHovered(false);

  return (
    <TableCell align={isGroupBy ? 'left' : 'right'}>
      <Cols style={{ justifyContent: getColumnAlignment(valueType) }}>
        <Col>
          <Text
            small
            bold
            style={{
              color: isOrderedBy ? publicaColors.darkBlue : 'inherit',
            }}
          >
            <Tooltip disableHoverListener={!tooltipMessage} title={tooltipMessage} placement='top'>
              {isSelectable ? (
                <span
                  style={{
                    cursor: 'pointer',
                    ...headerStyle,
                  }}
                  onMouseEnter={onHover}
                  onMouseLeave={onLeave}
                  onClick={onClick}
                >
                  {isGroupBy && `${colName} `}
                  <Span
                    style={{
                      fontSize: '9px',
                      position: 'absolute',
                      bottom: 0,
                      right: '-12px',
                    }}
                  >
                    {isOrderedBy
                      ? activeArrow(isHovered, orderDirection)
                      : inactiveArrow(isHovered, defaultOrderDirection)}
                  </Span>
                  {!isGroupBy && `${alias || label}`}
                </span>
              ) : (
                <span
                  style={{
                    color: publicaColors.gray,
                    cursor: 'default',
                    ...headerStyle,
                  }}
                >
                  {colName}
                </span>
              )}
            </Tooltip>
          </Text>
        </Col>
      </Cols>
    </TableCell>
  );
};

interface IRowDataProps {
  row: any;
  metricKeys: string[];
  metricValueTypes: ITableMetricValueTypes;
  eCurrencyType?: ECurrencyType;
  areRowsUpdating: boolean;
  colWidth: string;
  areRowsClickable: boolean;
  onClick: (row: any) => void;
}

const RowData = ({
  row,
  metricKeys,
  metricValueTypes,
  eCurrencyType,
  areRowsUpdating,
  colWidth,
  areRowsClickable,
  onClick,
}: IRowDataProps) => {
  return (
    <TableRow
      key={row.Name}
      hover
      style={{ cursor: areRowsClickable ? 'pointer' : 'default' }}
      onClick={() => {
        if (areRowsClickable) {
          onClick(row);
        }
      }}
    >
      <TableCell
        component='th'
        scope='row'
        style={{ color: areRowsClickable ? publicaColors.darkBlue : 'inherit' }}
      >
        {areRowsUpdating ? <Skeleton variant='text' animation='wave' /> : row.Name}
      </TableCell>
      {metricKeys.map((key) => {
        const valueType = metricValueTypes[key].valueType;
        return (
          <TableCell align='right' style={{ width: colWidth }}>
            {areRowsUpdating ? (
              <Skeleton variant='text' animation='wave' />
            ) : (
              <Cols style={{ justifyContent: getColumnAlignment(valueType) }}>
                <Col
                  style={{
                    display: 'flex',
                    whiteSpace:
                      valueType === valueTypes.CURRENCY || valueType === valueTypes.PERCENT_CHANGE
                        ? 'nowrap'
                        : 'normal',
                  }}
                >
                  <span style={styleCellValue(row[key], valueType)}>
                    {formatCellValue(row[key], valueType, eCurrencyType)}
                  </span>
                </Col>
              </Cols>
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

interface IDrillTableProps {
  title?: string | React.FunctionComponent;
  cols: ITableCol[];
  rows: Array<Record<string, any>>;
  metricKeys: string[];
  metricValueTypes: ITableMetricValueTypes;
  eCurrencyType?: ECurrencyType;
  orderBy: ITableMetric;
  orderDirection: OrderDirectionType;
  totalRows: number;
  rowsPerPageOptions?: number[];
  rowsPerPage?: number;
  page?: number;
  areRowsUpdating: boolean;
  areRowsClickable?: boolean;
  // if sending all data set to true; if sending paginated data, set to false
  areRowsAllData?: boolean;
  handleColClick?: (col: ITableCol) => void;
  handleRowClick: (row: Record<string, any>) => void;
  handleChangePage?: (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  handleChangeRowsPerPage?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleTableDisplayChange?: (rowIDs: string[]) => void;
}

export const DrillTable = ({
  title,
  cols,
  rows,
  metricKeys,
  metricValueTypes,
  eCurrencyType,
  orderBy,
  orderDirection,
  totalRows,
  rowsPerPageOptions = [10, 25, 50],
  rowsPerPage = 10,
  page = 0,
  areRowsUpdating,
  areRowsClickable = false,
  areRowsAllData = false,
  handleColClick,
  handleRowClick,
  handleChangePage,
  handleChangeRowsPerPage,
  handleTableDisplayChange,
}: IDrillTableProps) => {
  const { t } = usePublicaTranslation();
  const isMounted = useRef(false);
  const [currentRows, setCurrentRows] = useState<any[]>(rows);
  const [currentPage, setCurrentPage] = useState(page);
  const [currentRowsPerPage, setCurrentRowsPerPage] = useState(rowsPerPage);
  const [currentOrderDirection, setCurrentOrderDirection] = useState(
    areRowsAllData ? ORDER_DIRECTION.DESC : orderDirection,
  );
  const [currentOrderBy, setCurrentOrderBy] = useState<
    TableGroupByKey | TableGroupByKeyForReportsDashboard | TableMetricKey
  >(orderBy.label);

  const colWidth = `${Math.round(70 / (cols.length - 1))}%`;
  const dataRows = areRowsAllData
    ? rows.slice(
        currentPage * currentRowsPerPage,
        currentPage * currentRowsPerPage + currentRowsPerPage,
      )
    : currentRows;

  // whenever new rows are passed in we reset the page back to zero
  useEffect(() => {
    if (isMounted.current && areRowsAllData) {
      setCurrentPage(0);
    }
  }, [rows]);

  // whenever the displayed rows change, we can fire a handle to transmit the change
  useEffect(() => {
    if (dataRows.length > 0 && handleTableDisplayChange) {
      const rowIDs: string[] = [];
      dataRows.forEach((row: any) => {
        rowIDs.push(row.ID);
      });
      handleTableDisplayChange(rowIDs);
    }
  }, [dataRows]);

  // if rows are being updated, we reset the order by and order direction
  useEffect(() => {
    if (isMounted.current) {
      unstable_batchedUpdates(() => {
        setCurrentOrderBy(orderBy.label);
        setCurrentOrderDirection(orderDirection);
      });
    } else {
      isMounted.current = true;
    }
  }, [areRowsUpdating]);

  const handleChangeCurrentRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (areRowsAllData) {
      unstable_batchedUpdates(() => {
        setCurrentRowsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
      });
    } else {
      if (handleChangeRowsPerPage) {
        handleChangeRowsPerPage(event);
      }
    }
  };

  const handleChangeCurrentPage = (
    e: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    if (areRowsAllData) {
      setCurrentPage(newPage);
    } else {
      if (handleChangePage) {
        handleChangePage(e, newPage);
      }
    }
  };

  const handleColumnSortClick = (col: ITableCol) => {
    const key = col.label;
    const direction = getDirection(
      col.defaultOrderDirection,
      currentOrderBy === key,
      currentOrderDirection,
    );
    if (col.isTableControllingSort) {
      const sortedRows = sortRows({
        rows,
        key,
        direction,
        isName: col.valueType === valueTypes.NAME,
      });
      unstable_batchedUpdates(() => {
        setCurrentRows([...sortedRows]);
        setCurrentPage(0);
        if (key === currentOrderBy) {
          setCurrentOrderDirection(direction);
        } else {
          setCurrentOrderBy(key);
          setCurrentOrderDirection(col.defaultOrderDirection);
        }
      });
    } else {
      if (col.label !== currentOrderBy && col.label === orderBy.label) {
        const sortedRows = sortRows({
          rows,
          key,
          direction: orderDirection,
          isName: col.valueType === valueTypes.NAME,
        });
        unstable_batchedUpdates(() => {
          setCurrentOrderBy(key);
          setCurrentOrderDirection(orderDirection);
          setCurrentRows([...sortedRows]);
        });
      } else {
        if (handleColClick) {
          handleColClick(col);
        }
      }
    }
  };
  return (
    <Rows>
      {title && (
        <Row largePadding>
          <Text subheader style={{ textAlign: 'center' }}>
            {title}
          </Text>
        </Row>
      )}
      <Row noPadding>
        <TableContainer>
          <Table size='small'>
            {/* COLUMNS */}
            <TableHead>
              <TableRow style={{ verticalAlign: 'bottom' }}>
                {cols.map((col, i) => {
                  const {
                    label,
                    alias,
                    defaultOrderDirection,
                    tooltipMessage,
                    headerStyle,
                    isTableControllingSort,
                    tKey,
                  } = col;
                  return (
                    <ColHeader
                      orderDirection={
                        isTableControllingSort ? currentOrderDirection : orderDirection
                      }
                      defaultOrderDirection={defaultOrderDirection}
                      isGroupBy={i === 0}
                      isOrderedBy={
                        col.isTableControllingSort
                          ? label === currentOrderBy
                          : label === orderBy.label
                      }
                      isSelectable={col.isSelectable}
                      isTableControllingSort={!!col.isTableControllingSort}
                      label={label}
                      alias={alias}
                      onClick={() => handleColumnSortClick(col)}
                      tooltipMessage={tooltipMessage}
                      headerStyle={headerStyle}
                      valueType={col.valueType}
                      tKey={tKey}
                    />
                  );
                })}
              </TableRow>
            </TableHead>
            {/* ROWS */}
            <TableBody>
              {dataRows.map((row: any) => (
                <RowData
                  row={row}
                  metricKeys={metricKeys}
                  metricValueTypes={metricValueTypes}
                  eCurrencyType={eCurrencyType}
                  areRowsUpdating={areRowsUpdating}
                  colWidth={colWidth}
                  onClick={handleRowClick}
                  areRowsClickable={areRowsClickable}
                />
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                {/* The below @ts-ignore bypasses an MUI issue. */}
                {/* @ts-ignore */}
                <TablePagination
                  rowsPerPageOptions={rowsPerPageOptions}
                  count={totalRows}
                  rowsPerPage={areRowsAllData ? currentRowsPerPage : rowsPerPage}
                  page={areRowsAllData ? currentPage : page}
                  labelRowsPerPage={t('Rows')}
                  onChangePage={handleChangeCurrentPage}
                  onChangeRowsPerPage={(
                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                  ) => handleChangeCurrentRowsPerPage(e)}
                  style={{ borderBottom: 'none' }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Row>
    </Rows>
  );
};
